/**
 * App search (owner, 2026-10-12: "full app search to quickly find settings and jump to screens from mobile and on the computer, where
 * every screen and log which is active can be searched, suggested, and go direct to it"). One index, used by the web (⌘K, "/", the
 * header box) and the phone (More's search row, the icon on Today). Pure: the apps hand in what they already loaded.
 *
 * | AS1 From data | Screens come from the route lists the apps are built from (generated WEB_ROUTES / PHONE_ROUTES). Every route is either in
 * |               | WEB_SCREENS / PHONE_SCREENS or in a NOT_SEARCHABLE list with its reason; a test fails on a route in neither, so a new screen
 * |               | can't ship unfindable. Logs, people, places, records and guides come from the business's own data. |
 * | AS2 Role-aware | A screen shows only where the router itself would open it: App.tsx's RequireRole / RequireSwitch (generated WEB_ROUTE_GATES),
 * |                | the owner's switches (core ownership.ts canDo), and the same role floors the More list uses. Never a screen they can't use. |
 * | AS3 Active only | A log is a search result only when it is on today's list for this place (the server's expected checks): a log that isn't
 * |                 | on isn't there. Picking one opens it ready to log (/do?check=KEY, the same link Today and a reminder use). |
 * | AS4 Private (T4) | No check-in answers, no health check-in log, no sick or health report and no check-in record is ever an entry. |
 * | AS5 Typo-tolerant | The finder's matcher (findmatch.js: one-letter typos from 4 letters, two from 7, word prefixes while typing). |
 * | AS6 Plain words | Titles are what the screen says; actions start with the verb ("Report a problem", "Invite someone"). |
 * | AS7 Suggested | Before typing: today's checks that are due (or late), then the screens this person opens most on this device, else
 * |               | their role's usual ones. |
 */
import { norm, score } from "./findmatch.js";
import type { RouteGate } from "./appevents.ts";
import { WEB_ROUTES, WEB_ROUTE_GATES } from "./generated/appcontrols-web.ts";
import { PHONE_ROUTES } from "./generated/appcontrols-mobile.ts";
import type { Role } from "./model.ts";
import { canDo, type Switch, type Switches } from "./ownership.ts";
import { printouts, type PrintPlace } from "./printouts.ts";
import { citationItemsFor } from "./printsearch.ts";

export type SearchKind = "screen" | "setting" | "log" | "person" | "place" | "record" | "guide" | "action" | "print";
/** The type label on each result. */
export const SEARCH_KIND_LABEL: Readonly<Record<SearchKind, string>> = {
  screen: "Screen", setting: "Setting", log: "Log", person: "Person", place: "Place", record: "Record", guide: "Guide", action: "Action", print: "Print",
};
/**
 * An icon by meaning (the web's icon names; the phone maps them to its own set, with the kind's icon as the fallback).
 */
export type SearchIcon =
  | "spark" | "do" | "report" | "learn" | "people" | "today" | "fridge" | "pin" | "wrench" | "box" | "camera" | "cart" | "prove" | "book"
  | "sign" | "paper" | "settings" | "card" | "eye" | "handoff" | "bell" | "text" | "thermo" | "flask" | "allergen" | "clock" | "plus"
  | "search" | "check" | "restroom" | "bug" | "hands" | "trash" | "spray" | "plate" | "calendar" | "flag" | "write" | "phone";
export const KIND_ICON: Readonly<Record<SearchKind, SearchIcon>> = {
  screen: "search", setting: "settings", log: "check", person: "people", place: "pin", record: "book", guide: "learn", action: "spark", print: "sign",
};

export type SearchPlatform = "web" | "phone";

export interface SearchEntry {
  /** Stable: "screen:/food", "log:cold_units", "person:<id>". Also the key for "most used". */
  id: string;
  kind: SearchKind;
  title: string;
  /** One line under the title. */
  hint: string;
  /** Where Enter or a tap goes: an app path, or "location:<id>" (switch to that location). */
  to: string;
  icon: SearchIcon;
  /** Extra words that find it (synonyms), lowercase. */
  words: string[];
  /** A log on today's list: when it is due and whether it is done (AS7). */
  due?: { at: string; done: boolean };
  /** A printout (core printouts.ts): the one action on its row ("Open", "Print", "Download"), shown where the type label is. */
  action?: string;
  /** A printout: the FDA Form 3-A items it is tied to (a source in the repo ties each; printouts.ts P2). */
  items?: number[];
}

/** Who is searching (AS2). */
export interface SearchAccess {
  role: Role;
  /** The owner's switches for this person (core ownership.ts), when the server sent them. */
  switches?: Partial<Switches>;
  /** The app's own switch check (web session `may`), used instead of `switches` when given: one check, the router's. */
  may?: (sw: Switch) => boolean;
  /** Billing also opens for whoever sees the cost (added the card, or a location's billing admin). */
  seesCost?: boolean;
  /** A home: its own routes, no roles. */
  home: boolean;
  /** On DATA_ADMINS (me.dataAdmin): the admin Data page. */
  dataAdmin?: boolean;
}

const RANK: Readonly<Record<Role, number>> = { staff: 0, manager: 1, admin: 2, owner: 3, setup: -1 };
const atLeast = (role: Role, min: Role | undefined) => !min || RANK[role] >= RANK[min];
const mayUse = (a: SearchAccess, sw: Switch) => (a.may ? a.may(sw) : canDo({ role: a.role, ...(a.switches ? { permissions: a.switches } : {}) }, sw)) || (sw === "billing" && !!a.seesCost);

/** A searchable screen. `min` and `sw` only ever tighten what the router allows (AS2), never loosen it. */
export interface ScreenMeta {
  title: string;
  hint: string;
  words?: string;
  icon: SearchIcon;
  kind?: "screen" | "setting";
  /** A floor on top of the route's own gate (the More list's: an hourly worker has no use for the order list). */
  min?: Role;
  sw?: Switch;
  /** Only in a business or only in a home (the phone's routes; the web's come from App.tsx). */
  only?: "business" | "home";
  /** The admin Data page: DATA_ADMINS only. */
  dataAdmin?: true;
}

const S = (title: string, hint: string, icon: SearchIcon, words = "", extra: Partial<ScreenMeta> = {}): ScreenMeta => ({ title, hint, icon, words, ...extra });
const SET = (title: string, hint: string, icon: SearchIcon, words = "", extra: Partial<ScreenMeta> = {}): ScreenMeta => ({ title, hint, icon, words, kind: "setting", ...extra });

/** Synonyms shared by both apps (owner's examples: "alerts" → Notifications, "card" → Plan and billing, "staff" → People). */
const W = {
  notifications: "notifications notification notif notify alerts alert reminders reminder push email emails bell texts quiet hours digest unsubscribe",
  billing: "card credit card debit payment pay invoice invoices receipt subscription plan price cost billing charge stripe",
  people: "staff team employees employee workers crew members member roles role invite invites hire",
  today: "home now next feed start what to do",
  checks: "checks logs log record temperature temp fridge sanitizer checklist due",
  report: "report problem issue broken spill pest complaint wrong",
  food: "food fridge freezer dates expire expiring use by label leftovers",
  cleaning: "cleaning clean zones wipe mop sanitize scrub",
  equipment: "equipment machine machines tools tool thermometer thermometers inventory espresso grinder dishwasher ice machine oven grill fryer how to use add",
  supplies: "supplies stock soap gloves towels chemicals restock",
  binder: "binder inspection inspector proof records pdf print audit health department",
  learn: "learn lessons training course food handler permit library",
  settings: "settings preferences setup configure options",
} as const;

/**
 * The web's screens, by App.tsx route (the generated WEB_ROUTES). Who may open each comes from WEB_ROUTE_GATES; `min` here only
 * adds the More list's floor.
 */
export const WEB_SCREENS: Readonly<Record<string, ScreenMeta>> = {
  "": S("Today", "What to do now, and what's next", "spark", W.today),
  "activity": S("My work and points", "What you did this week, on time or late", "spark", "activity my work points this week history"),
  "admin/data": S("Data", "What we hold for each county", "book", "admin data counties", { dataAdmin: true }),
  "allergens/menu": S("Allergen menu", "The menu with its allergens, to print or share", "allergen", "allergen menu allergy print qr customers"),
  "allergens/photo": S("Photograph your menu", "We read each dish; you confirm its allergens", "camera", "menu photo scan dishes allergens"),
  "allergens/setup": SET("Allergens in your menu", "Which dishes have which allergens", "allergen", "allergens allergy allergies dishes menu peanut gluten dairy"),
  "allergy-order": S("Start an allergy order", "A guest's allergy: the safe way to make it", "allergen", "allergy order guest allergic customer peanut gluten"),
  "audit-trail": S("Audit trail", "Who did what, and when", "book", "audit trail history log who changed"),
  "billing": SET("Plan and billing", "Who you pay for, your card and invoices", "card", W.billing),
  "billing/cancel": SET("Cancel the plan", "Stop paying, and what happens to your records", "card", "cancel stop plan subscription end"),
  "carrying": S("Who's carrying what", "The checks given to each person on shift", "handoff", "carrying assigned delegate who has what tasks given"),
  "check-in": S("Do my daily check-in", "Before your shift", "hands", "check in checkin daily start shift"),
  "clean": S("Clean the whole place", "Room by room, ready to open", "spray", "clean whole place all rooms deep clean ready to open"),
  "content": S("Questions we don't cover yet", "Questions your team asked that our guides didn't answer", "text", "questions unanswered ask", { min: "admin" }),
  "do": S("Checks", "Every check due today, and the logs", "do", W.checks),
  "food": S("Food", "What's in the fridge, and when it goes", "fridge", W.food),
  "food/cooling": S("Cooling", "Start cooling a batch; the 2- and 6-hour readings", "thermo", "cooling cool down batch soup rice 2 hour 6 hour"),
  "food/label": S("Label food", "Print or write a date label", "write", "label sticker date mark day dot prep"),
  "have": S("What do you have?", "Room by room: what's in your home", "box", "have inventory things home"),
  "health": S("Place health", "Every area and room at a glance, and when it was last inspected", "prove", "health place location areas rooms inspection ready grade score"),
  "health-agreement": S("Employee health agreement", "Each employee's signed copy of the health agreement (Form 1-B)", "sign", "employee health agreement form 1-b signed copy", { min: "manager" }),
  "home-tune": SET("Your home's details", "Who lives here, rooms and pets", "settings", "home details setup tune rooms pets kids"),
  "insights": S("Busy hours", "Patterns from your records", "spark", "insights busy hours patterns analytics stats charts"),
  "inspection": S("Your last inspection", "What the county found, and what to fix", "prove", "inspection last inspector violations county report grade"),
  "invite-business": S("Invite a business", "Share CleanCleaver with another place", "text", "refer referral invite business friend share"),
  "kits": S("Event kits", "What to pack for a booth or event", "box", "kits event booth pack list catering"),
  "learn": S("Learn", "The state course, lessons and library", "learn", W.learn),
  "learn/library": S("Food safety library", "Search every guide", "book", "library guides food code rules how to"),
  "learn/playbook": S("Playbook", "How your place runs, written down", "book", "playbook sop procedures how we do it"),
  "learn/spills": S("Spills", "How to clean up each kind of spill", "spray", "spills spill cleanup broken glass"),
  "learn/stains": S("Stains", "How to get each stain out", "spray", "stains stain remove"),
  "learn/surfaces": S("Surfaces and equipment", "How to clean each surface", "spray", "surfaces surface counter floor steel cutting board"),
  "me": SET("Profile", "Your name, your permit, °F or °C, sign out", "people", "me profile account my name temperature unit fahrenheit celsius sign out permit"),
  "me/notifications": SET("Notifications", "What you get, and how", "bell", W.notifications),
  "messages": S("Messages and support", "Messages from CleanCleaver; ask us", "bell", "messages support help contact inbox"),
  "more": S("More", "Everything that isn't a tab", "spark", "more menu all"),
  "objects": S("Equipment", "How to use and clean each machine, thermometers too; add what you have", "wrench", W.equipment),
  "objects/guide": S("Equipment guide", "Open or close each machine, step by step", "wrench", "open close machines guide steps"),
  "objects/states": S("Equipment that needs attention", "What's out of order or overdue", "wrench", "broken out of order overdue attention repair"),
  "open": S("Open legally", "Permits and the checklist to open", "sign", "open legally permit permits license opening checklist"),
  "order": S("Order list", "What's low, by supplier", "cart", "order ordering supplier low reorder buy shopping", { min: "manager" }),
  "paper": S("Paper logs", "Print sheets, snap photos of them", "paper", "paper sheets print snap photo clipboard"),
  "paper/review": S("Check the photos", "Snapped sheets to confirm", "camera", "review photos sheets snapped"),
  "people": S("People", "Your team, invites, roles, and how to reach them", "people", W.people),
  "people/add": S("Invite someone", "Add a person to your team", "plus", "add invite someone person new hire join"),
  "people/permissions": SET("Permissions", "Who may do what: billing, people, settings", "settings", "permissions access who can switches admin transfer ownership"),
  "places": S("Cleaning", "What to clean, and where", "pin", W.cleaning),
  "points": S("Points", "Your points and the team goal", "spark", "points score goal rewards"),
  "prep": S("Set up a place for someone else", "Walk their rooms, then hand it over", "handoff", "prep set up someone else another place hand off"),
  "privacy": SET("Your data and privacy", "Download, choices, delete", "eye", "privacy data download export delete account choices cookies"),
  "products": S("Products you use", "The cleaners and supplies you picked", "box", "products brands cleaners chemicals"),
  "prove": S("Inspection binder", "One PDF, the way an inspector reads it", "prove", W.binder),
  "reach": SET("How should we reach you?", "How each kind of reminder gets to you", "phone", "reach contact phone number mobile cell text sms"),
  "recall": S("Recall check", "A food recall: check what you have", "flag", "recall recalled fda product"),
  "report": S("Report", "Report a problem, and see what was reported", "report", W.report),
  "rooms": S("Rooms", "Each room: what to do now", "pin", "rooms room board"),
  "rooms/ai-check": S("AI RoomCheck", "Photos of a room, checked for you", "camera", "ai room check photo"),
  "rooms/where": S("Where should this go?", "Where each thing belongs at home", "search", "where put store belongs"),
  "settings": SET("Settings", "Hours, county, how much gets written down, supplies", "settings", W.settings),
  "settings/locations/new": S("Add a location", "Another shop, truck or kitchen", "plus", "add location new store branch second place"),
  "signs": S("Signs and forms", "What to post and keep", "sign", "signs posters forms post wall handwashing sign"),
  "start/ready": S("Setup steps", "What setup filled in, and the first things to do", "check", "setup steps getting started first things onboarding"),
  "supplies": S("Supplies", "What you have, and where it's kept", "box", W.supplies),
  "supplies/buy": S("Buy by room", "What each room needs", "cart", "buy shop room needs"),
  "supplies/check": S("Stock check", "Enough, low, or out, one product at a time", "box", "stock check count supplies"),
  "supplies/kit": S("Inspection supplies", "What an inspector checks is on hand", "cart", "kit inspection supplies test strips thermometer"),
  "supplies/setup": SET("Set up supplies", "What you stock and where it's kept", "box", "supplies setup storage places shelves"),
  "team": S("Team", "Who's on shift, and how today is going", "people", "team shift on shift who is working staff"),
  "team/day": S("Close of day", "How today went, before you close", "clock", "close of day closing end of day summary"),
  "team/logs": S("Review logs", "Look over today's records", "check", "review logs records check over sign off"),
  "today": S("Today's full list", "Everything due today", "today", "today full list everything due", { min: "manager" }),
  "walk": S("Walk the rooms", "What's in place, and what to add", "camera", "walk rooms photos setup", { min: "manager" }),
  "walk/review": S("Review the setup", "What the room walk found", "camera", "review setup walk", { min: "manager" }),
  "workspaces": S("Switch business or home", "Your businesses and home", "handoff", "switch business home workspace another account"),
  "workspaces/new/business": S("Set up another business", "A second business, its own plan", "plus", "new business another add set up"),
  "workspaces/new/home": S("Set up your home", "Your home's food and cleaning", "plus", "home house new set up"),
};

/** Web routes that are not a place to jump to, each with why (AS1). A route in neither list fails the coverage test. */
export const WEB_NOT_SEARCHABLE: Readonly<Record<string, string>> = {
  "activity/:employeeId": "one person's page (opened from Team)",
  "admin/compliance": "our staff's page (the server answers 404 to everyone else)",
  "admin/waitlist": "our staff's page (the server answers 404 to everyone else)",
  "allergen-menu/:orgId/:locationId": "the customers' QR page",
  "allergens/setup/:id": "one dish (opened from Allergens)",
  "billing/back": "the win-back email's one button (opened from the email)",
  "billing/done": "where Stripe sends people back",
  "billing/start": "the step before Stripe",
  "do/fix/:entryId": "one fix (opened from its check)",
  "f/:token": "a food label's QR",
  "fix/:item": "\"Fix and log it now\" from the site (opened from the link on a cited item)",
  "food/act/:id": "one food (opened from the list)",
  "google/callback": "where Google sends the owner back",
  "guest-notes": "opened from a guest's note (the For you card and its push); the inbox answers only while the GUEST_NOTES switch is on, so search does not offer it",
  "guide/:token": "a visitor's guide link",
  "invite/:inviteId": "an invite link",
  "join": "typing a join code (signed in, you're already on a team)",
  "kids": "a child's turn (no search on purpose: a child can't wander off)",
  "kits/:kitId": "one kit (opened from Event kits)",
  "learn/how/:templateId": "one how-to (each log's own; found through its log)",
  "learn/lesson/:lessonId": "one lesson (opened from Learn)",
  "learn/library/:topicId": "one guide (each is a Guide result)",
  "learn/sign/:ackId": "one sign-off (opened from Learn)",
  "learn/spills/:slug": "one spill (opened from Spills)",
  "learn/stains/:slug": "one stain (opened from Stains)",
  "learn/surfaces/:surfaceId": "one surface (opened from Surfaces)",
  "objects/:id": "one machine (opened from Equipment)",
  "objects/run/:phase": "a run through the machines (opened from the Equipment guide)",
  "ownership/accept": "accepting an ownership offer (opened from its email)",
  "pantry": "an old address (now Food)",
  "people/:id": "one person (each is a Person result)",
  "places/:id": "one place (opened from Cleaning)",
  "playbook": "an old address (now Learn → Playbook)",
  "prep/:orgId": "one place being set up (opened from the list)",
  "print": "an old address (now Paper logs)",
  "rooms/check/:room": "one room (each is a Place result)",
  "rooms/check/:room/contents": "what's in one room (opened from the room)",
  "rooms/guide/:sopId": "one home guide (opened from Rooms)",
  "s/:code": "a printed sheet's QR",
  "steps/:procedureId": "the link in a 'needs help' message (opens that guide at that step)",
  "s/about": "about a printed sheet's QR",
  "see/:token": "a 'See your place' link",
  "setup": "an old address (now Today)",
  "snap": "an old address (now Paper logs)",
  "snap/review": "an old address (now Paper logs → Check the photos)",
  "start/have": "a setup step (opened from Setup steps)",
  "start/open": "a setup step (\"We're open\", opened from Setup steps)",
  "start/stage": "a setup step (opened from Setup steps)",
  "supplies/item/:key": "one supply (opened from Supplies)",
  "welcome": "the first screen after joining",
};

/** The phone's screens, by expo-router file (the generated PHONE_ROUTES). Phone screens gate themselves; `min` mirrors More's floors. */
export const PHONE_SCREENS: Readonly<Record<string, ScreenMeta>> = {
  "/": S("Today", "What to do now, and what's next", "spark", W.today, { only: "business" }),
  "/today": S("Today", "What to do now, and what's next", "spark", W.today, { only: "business" }),
  "/activity": S("My work and points", "What you did this week, on time or late", "spark", "activity my work points this week history", { only: "business" }),
  "/allergy-order": S("Start an allergy order", "A guest's allergy: the safe way to make it", "allergen", "allergy order guest allergic customer peanut gluten", { only: "business" }),
  "/ask-questions": S("Waiting for your answer", "Questions your team asked that our guides didn't answer", "text", "questions unanswered ask", { min: "manager", only: "business" }),
  "/audit-trail": S("Audit trail", "Who did what, and when", "book", "audit trail history log who changed", { min: "manager", only: "business" }),
  "/carrying": S("Who's carrying what", "The checks given to each person on shift", "handoff", "carrying assigned delegate who has what tasks given", { only: "business" }),
  "/certificates": S("Scan certificates", "Food handler cards, several in a row", "camera", "certificates certificate permits food handler permit scan", { min: "manager", only: "business" }),
  "/checkin": S("Do my daily check-in", "Before your shift", "hands", "check in checkin daily start shift", { only: "business" }),
  "/clean": S("Clean the whole place", "Room by room, ready to open", "spray", "clean whole place all rooms deep clean ready to open"),
  "/cooling": S("Cooling", "Start cooling a batch; the 2- and 6-hour readings", "thermo", "cooling cool down batch soup rice 2 hour 6 hour", { only: "business" }),
  "/delete-account": SET("Delete my account", "What goes, and what the law makes us keep", "eye", "delete account remove close privacy"),
  "/do": S("Checks", "Every check due today, and the logs", "do", W.checks, { only: "business" }),
  "/food": S("Food", "What's in the fridge, and when it goes", "fridge", W.food, { only: "business" }),
  "/health": S("Place health", "Every area and room at a glance, and when it was last inspected", "prove", "health place location areas rooms inspection ready grade score", { min: "manager", only: "business" }),
  "/health-agreement": S("Employee health agreement", "Each employee's signed copy of the health agreement (Form 1-B)", "sign", "employee health agreement form 1-b signed copy", { min: "manager", only: "business" }),
  "/inspection": S("Your last inspection", "What the county found, and what to fix", "prove", "inspection last inspector violations county report grade flies fix cited plan", { only: "business" }),
  "/home": S("Home", "Your home's score and today's chores", "spark", "home today score chores", { only: "home" }),
  "/home-have": S("What do you have?", "Room by room: what's in your home", "box", "have inventory things home", { only: "home" }),
  "/home-kits": S("Kits", "Equipment lists to share", "box", "kits lists equipment", { only: "home" }),
  "/home-setup": SET("Your home's details", "Who lives here, rooms and pets", "settings", "home details setup rooms pets kids", { only: "home" }),
  "/home-shopping": S("Shopping list", "What you still need, by room", "cart", "shopping list buy need amazon", { only: "home" }),
  "/home/guides": S("Guides", "How to clean and care for each thing", "learn", "guides how to clean", { only: "home" }),
  "/home/more": S("More", "Your home's details and settings", "spark", "more menu", { only: "home" }),
  "/home/pantry": S("Food", "Your food, oldest first", "fridge", W.food, { only: "home" }),
  "/insights": S("Busy hours", "Patterns from your records", "spark", "insights busy hours patterns analytics stats charts", { min: "manager", only: "business" }),
  "/invite": S("Invite someone", "Add a person to your team", "plus", "add invite someone person new hire join", { min: "manager", only: "business" }),
  "/invites": S("Invites", "Everyone invited, and who joined", "people", "invites invited waiting joined resend", { min: "manager", only: "business" }),
  "/join": S("Join a team", "Type a join code or scan the QR code", "plus", "join code team qr"),
  "/kiosk": SET("Shared tablet mode", "The shop tablet shows names; each person uses a PIN", "settings", "kiosk tablet shared pin ipad", { min: "manager", only: "business" }),
  "/label-food": S("Label food", "Print or write a date label", "write", "label sticker date mark day dot prep", { only: "business" }),
  "/learn": S("Learn and Library", "The state course, lessons, and searchable guides", "learn", W.learn, { only: "business" }),
  "/learning": S("Learn", "The state course, lessons and sign-offs", "learn", W.learn, { only: "business" }),
  "/lookup": S("Restaurant lookup", "A restaurant's last health inspection, free", "search", "restaurant lookup inspection health county find eat near me violations"),
  "/locations": SET("Locations", "Each location's county, hours and settings", "pin", "locations hours county address permit settings", { min: "admin", only: "business" }),
  "/location-new": S("Add a location", "Another shop, truck or kitchen", "plus", "add location new store branch second place", { min: "admin", only: "business" }),
  "/menu-photo": S("Photograph your menu", "We read each dish; you confirm its allergens", "camera", "menu photo scan dishes allergens", { min: "admin", only: "business" }),
  "/more": S("More", "Everything that isn't a tab", "spark", "more menu all profile", { only: "business" }),
  "/notifications": SET("Notifications", "What you get, and how", "bell", W.notifications),
  "/onboarding": S("Getting started", "Lessons, sign-offs and your permit", "learn", "getting started onboarding lessons sign permit", { only: "business" }),
  "/opening": S("Open legally", "Permits and the one question: can we open yet?", "sign", "open legally permit permits license opening checklist", { min: "manager", only: "business" }),
  "/order": S("Order list", "What's low, by supplier", "cart", "order ordering supplier low reorder buy shopping", { only: "business" }),
  "/check-settings": SET("Checks: timing and photos", "How often each check runs, and how much photo proof", "clock", "timing how often interval hours every photo proof picture fridge cleaning sanitizer", { min: "manager", only: "business" }),
  "/our-way": SET("How we do it here", "Your own way of doing a job", "write", "our way how we do it custom steps", { min: "manager", only: "business" }),
  "/outbox": S("Not sent yet", "Saved on this phone, waiting to send", "clock", "outbox waiting send offline pending queue"),
  "/people": S("People", "Your team, invites, roles, and how to reach them", "people", W.people, { min: "manager", only: "business" }),
  "/places": S("Cleaning", "What to clean, zone by zone", "pin", W.cleaning, { only: "business" }),
  "/plan": SET("Plan and billing", "Who you pay for, your card and invoices", "card", W.billing, { sw: "billing", only: "business" }),
  "/prep": S("Set up a place for someone else", "Walk their rooms, then hand it over", "handoff", "prep set up someone else another place hand off", { min: "manager" }),
  "/prove": S("Inspection binder", "The rules for this place and what's on file", "prove", W.binder, { min: "manager", only: "business" }),
  "/reach": SET("How should we reach you?", "How each kind of reminder gets to you", "phone", "reach contact phone number mobile cell text sms", { only: "business" }),
  "/recall": S("Recall check", "A food recall: check what you have", "flag", "recall recalled fda product", { min: "manager", only: "business" }),
  "/referrals": S("Invite a business", "Text a friend your link", "text", "refer referral invite business friend share"),
  "/report": S("Report", "Report a problem, and see what was reported", "report", W.report, { only: "business" }),
  "/reports": S("Reports", "What was reported here, and what was done", "report", "reports reported handled list", { only: "business" }),
  "/rooms": S("Rooms", "Each room: what to do now", "pin", "rooms room board"),
  "/sanitizer": SET("How do you sanitize?", "Sets your team's sanitizer check", "flask", "sanitizer bleach quat chlorine test strips dispenser", { min: "manager", only: "business" }),
  "/setup-scan": S("Set up by photo", "Snap your kitchen or a receipt", "camera", "setup photo scan kitchen receipt", { min: "manager", only: "business" }),
  "/shop-kit": S("Inspection supplies", "What an inspector checks is on hand", "cart", "kit inspection supplies test strips thermometer", { min: "manager", only: "business" }),
  "/signs": S("Signs and forms", "What to post and keep", "sign", "signs posters forms post wall handwashing sign", { min: "manager", only: "business" }),
  "/snap": S("Paper logs", "Snap a paper sheet; it becomes records", "paper", "paper sheets snap photo clipboard", { only: "business" }),
  "/stock-check": S("Stock check", "Enough, low, or out, one product at a time", "box", "stock check count supplies", { only: "business" }),
  "/surfaces": S("Surfaces and equipment", "How to clean each surface", "spray", "surfaces surface counter floor steel cutting board", { only: "business" }),
  "/team": S("Team", "Who's on shift, and how today is going", "people", "team shift on shift who is working staff", { min: "manager", only: "business" }),
  "/things": S("Equipment", "How to use and clean each machine, thermometers too; add what you have", "wrench", W.equipment),
  "/things/guide": S("Equipment guide", "Open or close each machine, step by step", "wrench", "open close machines guide steps"),
  "/things/states": S("Equipment that needs attention", "What's out of order or overdue", "wrench", "broken out of order overdue attention repair", { min: "manager" }),
  "/timers": S("Timers", "Cooling, thawing, the 4-hour rule, or your own", "clock", "timers timer alarm countdown thaw 4 hour", { only: "business" }),
  "/walk": S("Walk the rooms", "What's in place, and what to add", "camera", "walk rooms photos setup", { min: "manager", only: "business" }),
  "/walk-review": S("Review the setup", "What the room walk found", "camera", "review setup walk", { min: "manager", only: "business" }),
  "/workspaces": S("Switch business or home", "Your businesses and home", "handoff", "switch business home workspace another account"),
};

export const PHONE_NOT_SEARCHABLE: Readonly<Record<string, string>> = {
  "/cleanup/[kind]": "a list of spills or stains (opened from Learn)",
  "/cleanup/[kind]/[slug]": "one spill or stain",
  "/f/[token]": "a food label's QR",
  "/web-link": "an address that opens the website (opened from a link)",
  "/fix/[item]": "\"Fix and log it now\" from the site (opened from the link on a cited item)",
  "/home-guide": "one home guide (opened from Guides)",
  "/kiosk-lessons": "lessons on the shared tablet, for the person whose PIN opened it",
  "/library/[id]": "one guide (each is a Guide result)",
  "/onboarding/ack": "one sign-off (opened from Getting started)",
  "/onboarding/card": "one step of Getting started",
  "/onboarding/course": "one step of Getting started",
  "/onboarding/house": "one step of Getting started",
  "/onboarding/lesson": "one lesson (opened from Getting started)",
  "/onboarding/permit": "one step of Getting started",
  "/open": "a reminder's link (logwave://open?check=…)",
  "/checkout": "the US iPhone build's Checkout, opened only from the plan asks (apps/mobile logic/checkout.ts); never a search result, so no other build lists a purchase",
  "/places/[id]": "one place (opened from Cleaning)",
  "/quick-log": "a quick job on the shared tablet, for the person whose PIN opened it",
  "/room-check": "one room (each is a Place result)",
  "/room-contents": "what's in one room (opened from the room)",
  "/scan-label": "the camera for a food label's QR (opened from Food)",
  "/search": "this search itself",
  "/sign-in": "signing in",
  "/start": "the onboarding quiz (What brings you here?), before any sign-in, reached from the first screen; nothing in it to search for",
  "/spot-check": "a spot check photo (opened from Ask)",
  "/steps": "the link in a 'needs help' message (opens that guide at that step)",
  "/surfaces/[id]": "one surface (opened from Surfaces)",
  "/things/[id]": "one machine (opened from Equipment)",
  "/things/run": "a run through the machines (opened from the Equipment guide)",
};

/** The routes in neither list, and the list entries that are no longer routes (AS1). Empty = every screen is findable or excused. */
export function searchCoverage(platform: SearchPlatform, routes: readonly string[] = platform === "web" ? WEB_ROUTES : PHONE_ROUTES): { missing: string[]; stale: string[] } {
  const screens = platform === "web" ? WEB_SCREENS : PHONE_SCREENS;
  const skip = platform === "web" ? WEB_NOT_SEARCHABLE : PHONE_NOT_SEARCHABLE;
  const listed = new Set([...Object.keys(screens), ...Object.keys(skip)]);
  const have = new Set(routes);
  return {
    missing: routes.filter((r) => !listed.has(r)),
    stale: [...listed].filter((r) => !have.has(r)),
  };
}

const webPath = (route: string) => `/${route}`;

/** Can this person open this web route here (App.tsx's own gate plus the catalog's floor)? */
export function webRouteOpen(route: string, a: SearchAccess, meta?: ScreenMeta): boolean {
  const gates = WEB_ROUTE_GATES[route];
  const g: RouteGate | undefined = a.home ? gates?.home : gates?.business;
  if (!gates || !g) return false;
  if (meta?.dataAdmin && !a.dataAdmin) return false;
  if (a.home) return true; // a home has no roles (docs/HOME.md)
  if (!atLeast(a.role, g.min) || !atLeast(a.role, meta?.min)) return false;
  const sw = g.sw ?? meta?.sw;
  return !sw || mayUse(a, sw);
}
function phoneOpen(meta: ScreenMeta, a: SearchAccess): boolean {
  if (meta.only === "home" && !a.home) return false;
  if (meta.only === "business" && a.home) return false;
  if (a.home) return true;
  if (!atLeast(a.role, meta.min)) return false;
  return !meta.sw || mayUse(a, meta.sw);
}

function screenEntry(id: string, to: string, m: ScreenMeta): SearchEntry {
  return { id: `screen:${to}`, kind: m.kind ?? "screen", title: m.title, hint: m.hint, to, icon: m.icon, words: words(m.words ?? "") };
}
const words = (s: string) => s.toLowerCase().split(/\s+/).filter(Boolean);

/** Every screen and settings page this person may open, on this app (AS1, AS2). */
export function screenEntries(platform: SearchPlatform, a: SearchAccess): SearchEntry[] {
  const out: SearchEntry[] = [];
  const seen = new Set<string>();
  if (platform === "web") {
    for (const route of WEB_ROUTES) {
      const m = WEB_SCREENS[route];
      if (!m || !webRouteOpen(route, a, m)) continue;
      out.push(screenEntry(route, webPath(route), m));
    }
  } else {
    for (const route of PHONE_ROUTES) {
      const m = PHONE_SCREENS[route];
      if (!m || !phoneOpen(m, a)) continue;
      // "/" and "/today" are one Today: keep one.
      const key = `${m.title}|${m.hint}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(screenEntry(route, route === "/" ? "/today" : route, m));
    }
  }
  return out;
}

/** A setting inside a settings page, with the anchor that opens it (the web's Settings sections; the phone's own screens). */
interface SettingSpot { title: string; hint: string; icon: SearchIcon; words: string; web?: { route: string; hash: string }; phone?: string }
const SETTING_SPOTS: readonly SettingSpot[] = [
  { title: "Opening hours", hint: "Settings · When you open and close, each day", icon: "clock", words: "hours open close opening closing time schedule days holiday special hours", web: { route: "settings", hash: "hours" }, phone: "/locations" },
  { title: "County, address and permit", hint: "Settings · Your place's details", icon: "pin", words: "county address permit number expiry location name health department", web: { route: "settings", hash: "location" }, phone: "/locations" },
  { title: "Menu", hint: "Settings · What you serve", icon: "plate", words: "menu items dishes what you serve drinks", web: { route: "settings", hash: "menu" } },
  { title: "How much gets written down", hint: "Settings · Your logging level", icon: "check", words: "logging level how many checks frequency record keeping", web: { route: "settings", hash: "level" } },
  { title: "How we do it here", hint: "Settings · Your own way of doing a job", icon: "write", words: "our way how we do it custom steps", web: { route: "settings", hash: "our-way" } },
  { title: "How you sanitize", hint: "Settings · Sanitizer and test strips", icon: "flask", words: "sanitizer bleach quat chlorine test strips dispenser mix", web: { route: "settings", hash: "sanitizer" } },
  { title: "Who can do what", hint: "Settings · Members and roles", icon: "people", words: "members roles role who can admin manager staff", web: { route: "settings", hash: "members" } },
  { title: "Shop tablet mode", hint: "Settings · A shared tablet, a PIN each", icon: "settings", words: "kiosk tablet shared pin ipad", web: { route: "settings", hash: "group-team" } },
  { title: "Connected AI apps", hint: "Settings · Claude, ChatGPT and other apps", icon: "settings", words: "ai apps claude chatgpt connect disconnect", web: { route: "settings", hash: "group-team" } },
  { title: "Locations", hint: "Settings · Your locations, and add one", icon: "pin", words: "locations location stores branches", web: { route: "settings", hash: "locations" } },
  { title: "Share usage data", hint: "Settings · Help us improve, or not", icon: "eye", words: "usage data share analytics improve", web: { route: "settings", hash: "group-more" } },
  { title: "Temperature unit: °F or °C", hint: "Profile · Your own choice", icon: "thermo", words: "temperature unit fahrenheit celsius °f °c degrees", web: { route: "me", hash: "me-temp-unit" } },
];

export function settingEntries(platform: SearchPlatform, a: SearchAccess): SearchEntry[] {
  const out: SearchEntry[] = [];
  for (const s of SETTING_SPOTS) {
    let to: string | undefined;
    if (platform === "web" && s.web && webRouteOpen(s.web.route, a, WEB_SCREENS[s.web.route])) to = `${webPath(s.web.route)}#${s.web.hash}`;
    if (platform === "phone" && s.phone && PHONE_SCREENS[s.phone] && phoneOpen(PHONE_SCREENS[s.phone]!, a)) to = s.phone;
    if (to) out.push({ id: `setting:${s.title}`, kind: "setting", title: s.title, hint: s.hint, to, icon: s.icon, words: words(s.words) });
  }
  return out;
}

/** Quick actions, verb first (AS6). Each rides on the screen it opens, so it shows only where that screen does (AS2). */
interface QuickAction { id: string; title: string; hint: string; icon: SearchIcon; words: string; web?: string; phone?: string }
const ACTIONS: readonly QuickAction[] = [
  { id: "report", title: "Report a problem", hint: "Something broken, spilled or wrong", icon: "report", words: "report problem issue broken spill pest complaint wrong", web: "report", phone: "/report" },
  { id: "invite", title: "Invite someone", hint: "Add a person to your team", icon: "plus", words: "invite add someone person new hire join", web: "people/add", phone: "/invite" },
  { id: "label", title: "Label food", hint: "A date label for something you made or opened", icon: "write", words: "label sticker date mark day dot prep", web: "food/label", phone: "/label-food" },
  { id: "cool", title: "Start cooling a batch", hint: "The 2- and 6-hour readings, with a reminder", icon: "thermo", words: "cooling cool batch soup rice start", web: "food/cooling", phone: "/cooling" },
  { id: "binder", title: "Print the inspection binder", hint: "One PDF, the way an inspector reads it", icon: "prove", words: "print binder pdf inspector inspection proof", web: "prove", phone: "/prove" },
  { id: "allergy", title: "Start an allergy order", hint: "A guest's allergy: the safe way to make it", icon: "allergen", words: "allergy order allergic guest", web: "allergy-order", phone: "/allergy-order" },
  { id: "snap", title: "Snap a paper log", hint: "A photo of a paper sheet becomes records", icon: "camera", words: "snap photo paper sheet clipboard", web: "paper", phone: "/snap" },
  { id: "location", title: "Add a location", hint: "Another shop, truck or kitchen", icon: "plus", words: "add location new store branch", web: "settings/locations/new", phone: "/location-new" },
  { id: "switch", title: "Switch business or home", hint: "Your businesses and home", icon: "handoff", words: "switch business home workspace", web: "workspaces", phone: "/workspaces" },
];

export function actionEntries(platform: SearchPlatform, a: SearchAccess): SearchEntry[] {
  const out: SearchEntry[] = [];
  for (const x of ACTIONS) {
    let to: string | undefined;
    if (platform === "web" && x.web !== undefined && webRouteOpen(x.web, a, WEB_SCREENS[x.web])) to = webPath(x.web);
    if (platform === "phone" && x.phone && PHONE_SCREENS[x.phone] && phoneOpen(PHONE_SCREENS[x.phone]!, a)) to = x.phone;
    if (to) out.push({ id: `action:${x.id}`, kind: "action", title: x.title, hint: x.hint, to, icon: x.icon, words: words(x.words) });
  }
  return out;
}

/**
 * The Print group (core printouts.ts): every sign, paper log, the binder, the date label and the allergen menu this person may open,
 * each with what it is, where it goes and one action. Built from the Signs and forms, Paper logs and Binder screens' own data, so a
 * printout those screens show is here (test). A home prints none of these. Each rides on the screen it opens (AS2).
 */
export function printEntries(platform: SearchPlatform, place: PrintPlace, a: SearchAccess): SearchEntry[] {
  if (a.home) return [];
  const out: SearchEntry[] = [];
  for (const x of printouts(place)) {
    const to = platform === "web" ? x.web : x.phone;
    if (!to) continue;
    const route = to.split(/[?#]/)[0]!;
    const open = platform === "web" ? webRouteOpen(route.replace(/^\//, ""), a, WEB_SCREENS[route.replace(/^\//, "")]) : !!PHONE_SCREENS[route] && phoneOpen(PHONE_SCREENS[route]!, a);
    if (!open || (x.manager && !atLeast(a.role, "manager"))) continue;
    out.push({ id: `print:${x.id}`, kind: "print", title: x.title, hint: `${x.where} · ${x.what}`, to, icon: x.kind === "log" ? "paper" : x.kind === "binder" || x.kind === "followup" ? "prove" : x.kind === "label" ? "write" : x.kind === "menu" ? "allergen" : "sign", words: x.words, action: x.action, items: x.items });
  }
  return out;
}

/**
 * "For this citation": when the words are a violation (a cited plan's word like "flies", the form's item title like "cold holding",
 * "item 22", an inspector's phrase), the printouts tied to that item (printsearch.ts), signs first, then the log that proves the fix.
 * Empty when no item matches or none has a printout (printouts.ts P3).
 */
export function citationPrintEntries(entries: readonly SearchEntry[], query: string): SearchEntry[] {
  const items = citationItemsFor(query);
  if (!items.length) return [];
  const rank = (e: SearchEntry) => (e.id.startsWith("print:sign:") ? 0 : e.id.startsWith("print:log:") ? 1 : 2);
  return entries.filter((e) => e.kind === "print" && e.items?.some((n) => items.includes(n))).sort((a, b) => rank(a) - rank(b)).slice(0, 5);
}
/**
 * The result list with "For this citation" (the printouts tied to the violation's words): first, except under a "What do I do if…" answer
 * (momentEntries), which is the more urgent thing and stays on top. Each printout shows once. Empty groups are left out.
 */
export function citationGroups(results: readonly SearchEntry[], cite: readonly SearchEntry[]): { citation: boolean; items: SearchEntry[] }[] {
  const ids = new Set(cite.map((e) => e.id));
  const rest = results.filter((e) => !ids.has(e.id));
  const groups = rest[0]?.id.startsWith("moment:") ? [{ citation: false, items: rest }, { citation: true, items: [...cite] }] : [{ citation: true, items: [...cite] }, { citation: false, items: rest }];
  return groups.filter((g) => g.items.length > 0);
}

/** Words that find a log by what people call it (AS5). Templates not listed are found by their own title. */
export const LOG_WORDS: Readonly<Record<string, string>> = {
  cold_units: "fridge fridges refrigerator freezer cooler walk-in walk in reach-in cold holding temperature temperatures temp reading pastry case",
  temp_cook_hold: "hot holding cooking cook temperature temp reading hot food",
  cooling: "cooling cool down batch soup rice",
  sanitizer: "sanitizer sanitiser bucket test strip strips ppm quat chlorine bleach sanitize cloth",
  receiving: "delivery deliveries receiving truck arrived order",
  calibration: "thermometer calibrate calibration ice water probe",
  discard: "throw out toss waste discard thrown",
  cleaning_signoff: "cleaning clean sign off",
  pest_sightings: "pest pests bug bugs mouse mice roach roaches insect rodent",
  pest_check: "pest pests bug bugs mouse mice roach roaches insect rodent look",
  restroom_check: "restroom restrooms bathroom toilet soap towels",
  task_done: "checklist task chore",
  allergy_order: "allergy allergen order",
  storage_order: "fridge order shelf shelves raw below ready-to-eat",
  dry_storage: "dry storage shelf pantry",
  water_temp: "hot water sink water temperature",
  ice_check: "ice machine scoop",
  hands_glove: "hands gloves handwashing wash hands",
  transport_temp: "transport catering delivery on the road",
  produce_wash: "produce wash fruit vegetables",
};

/** Never in search (AS4, T4): the health check-in and the return-to-work record are private. */
export const SEARCH_PRIVATE_TEMPLATES: ReadonlySet<string> = new Set(["health_checkin", "ready_to_work"]);

/** A check on today's list for this place (the server's expected checks; web TodayResponse.expected, the phone's s.today.expected). */
export interface TodayCheck { key: string; label: string; templateId: string; taskId?: string; dueAt: string; satisfied: boolean }

/**
 * Logs for this place today (AS3): one entry per log (each fridge round is one "Log fridge and freezer temperatures"), one per checklist
 * item that is on. It opens the next one not done yet, ready to log; done for today, it opens the last one. A log that isn't on today's
 * list is not here at all.
 */
export function logEntries(platform: SearchPlatform, checks: readonly TodayCheck[], o: { clock: (iso: string) => string; now: Date; templateTitle?: (id: string) => string | undefined }): SearchEntry[] {
  const groups = new Map<string, TodayCheck[]>();
  for (const c of checks) {
    if (SEARCH_PRIVATE_TEMPLATES.has(c.templateId)) continue;
    const g = c.templateId === "task_done" ? `task:${c.taskId ?? c.key}` : c.templateId;
    groups.set(g, [...(groups.get(g) ?? []), c]);
  }
  const nowMs = o.now.getTime();
  const out: SearchEntry[] = [];
  for (const [g, list] of groups) {
    const sorted = [...list].sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt));
    const next = sorted.find((c) => !c.satisfied);
    const pick = next ?? sorted[sorted.length - 1]!;
    const tTitle = pick.templateId === "task_done" ? "Checklist" : o.templateTitle?.(pick.templateId);
    const late = next && Date.parse(next.dueAt) < nowMs;
    const when = !next ? "Done for today" : late ? `Was due ${o.clock(next.dueAt)}` : `Due ${o.clock(next.dueAt)}`;
    out.push({
      id: `log:${g}`, kind: "log", title: pick.label, hint: tTitle ? `${when} · ${tTitle}` : when,
      to: `/do?check=${encodeURIComponent(pick.key)}`, icon: logIcon(pick.templateId),
      words: words(`${LOG_WORDS[pick.templateId] ?? ""} ${tTitle ?? ""} log record write down`),
      due: { at: pick.dueAt, done: !next },
    });
  }
  return out;
}
function logIcon(t: string): SearchIcon {
  if (/cold|temp|cook|cool|calibr|water/.test(t)) return "thermo";
  if (/sanitiz/.test(t)) return "flask";
  if (/pest/.test(t)) return "bug";
  if (/restroom/.test(t)) return "restroom";
  if (/allerg/.test(t)) return "allergen";
  if (/discard/.test(t)) return "trash";
  return "check";
}

/** A person on the team (People rows), for someone who can open People (AS2). */
export interface SearchPerson { employeeId: string; name: string; title?: string | null; role?: Role | null; email?: string | null; status?: string }
export function personEntries(platform: SearchPlatform, people: readonly SearchPerson[], a: SearchAccess): SearchEntry[] {
  const peopleOpen = platform === "web" ? webRouteOpen("people", a, WEB_SCREENS["people"]) : phoneOpen(PHONE_SCREENS["/people"]!, a);
  if (!peopleOpen) return [];
  return people.filter((p) => p.name).map((p) => ({
    id: `person:${p.employeeId}`, kind: "person", title: p.name,
    hint: [p.title || (p.role ? ROLE_WORD[p.role] : ""), "Open their page"].filter(Boolean).join(" · "),
    to: platform === "web" ? `/people/${encodeURIComponent(p.employeeId)}` : "/people", icon: "people",
    words: words(`${p.email ?? ""} ${p.title ?? ""} ${p.role ?? ""} person`),
  }));
}
const ROLE_WORD: Readonly<Record<Role, string>> = { owner: "Owner", admin: "Admin", manager: "Manager", staff: "Staff", setup: "Setup" };

/** Places: the other locations (switch to one) and this place's rooms (open the room's check). */
export function placeEntries(platform: SearchPlatform, p: { locations: readonly { id: string; name: string }[]; currentId?: string; rooms: readonly { key: string; name: string; line?: string }[] }): SearchEntry[] {
  const out: SearchEntry[] = [];
  if (p.locations.length > 1) for (const l of p.locations) {
    if (l.id === p.currentId) continue;
    out.push({ id: `place:loc:${l.id}`, kind: "place", title: `Switch to ${l.name}`, hint: "Location · Open its checks and records", to: `location:${l.id}`, icon: "pin", words: words(`${l.name} location store switch`) });
  }
  for (const r of p.rooms) out.push({
    id: `place:room:${r.key}`, kind: "place", title: r.name, hint: `Room · ${r.line ?? "Check this room"}`,
    to: platform === "web" ? `/rooms/check/${encodeURIComponent(r.key)}` : `/room-check?room=${encodeURIComponent(r.key)}`, icon: "pin",
    words: words(`room ${r.key.replace(/[_-]/g, " ")} check`),
  });
  return out;
}

/** Today's records (core todayrecords.ts rows). Never a check-in or a report (AS4: a report can be a sick report). */
export interface SearchRecord { id: string; kind: string; title: string; line: string; time: string; who?: string; open?: { to: string; id: string } }
export function recordEntries(rows: readonly SearchRecord[], o: { pathOf: (r: SearchRecord) => string; templateIdOf?: (title: string) => string | undefined }): SearchEntry[] {
  const out: SearchEntry[] = [];
  const seen = new Set<string>();
  for (const r of rows) { // newest first (todayrecords D4): the first of each title is the last one done
    if (r.kind === "checkin" || r.kind === "report") continue;
    if (seen.has(r.title)) continue;
    seen.add(r.title);
    const t = o.templateIdOf?.(r.title);
    if (t && SEARCH_PRIVATE_TEMPLATES.has(t)) continue;
    out.push({
      id: `record:${r.id}`, kind: "record", title: `Last: ${r.title}`, hint: [r.line, r.time, r.who].filter(Boolean).join(" · "),
      to: o.pathOf(r), icon: "book", words: words(`last latest recent reading record entry today ${t ? LOG_WORDS[t] ?? "" : ""}`),
    });
  }
  return out;
}

/** Guides from the library (the place's state's own library). */
export function guideEntries(platform: SearchPlatform, topics: readonly { id: string; title: string; summary: string; keywords: readonly string[] }[]): SearchEntry[] {
  return topics.map((t) => ({
    id: `guide:${t.id}`, kind: "guide", title: t.title, hint: firstSentence(t.summary),
    to: platform === "web" ? `/learn/library/${encodeURIComponent(t.id)}` : `/library/${encodeURIComponent(t.id)}`, icon: "learn",
    words: t.keywords.map((k) => k.toLowerCase()),
  }));
}
const firstSentence = (s: string) => { const m = s.match(/^.*?[.!?](\s|$)/); const x = (m ? m[0] : s).trim(); return x.length > 90 ? `${x.slice(0, 88).trimEnd()}…` : x; };

/** The whole static part of the index for this person: screens, settings, actions. Data entries are added by the app. */
export function staticEntries(platform: SearchPlatform, a: SearchAccess): SearchEntry[] {
  // An action and the screen it opens, under the same name, are one result ("Invite someone" once): the action wins.
  const seen = new Set<string>();
  return [...actionEntries(platform, a), ...screenEntries(platform, a), ...settingEntries(platform, a)].filter((e) => { const k = `${e.to}|${e.title}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

/** Ties broken by kind: what people come to do first. */
const KIND_ORDER: Readonly<Record<SearchKind, number>> = { action: 0, log: 1, print: 1.5, screen: 2, setting: 3, person: 4, place: 5, record: 6, guide: 7 };

/** Words that ask for paper (searchEntries ranks printouts below screens and logs without one of them). */
const WANTS_PRINT = /\b(print|printing|paper|sheets?|signs?|posters?|forms?|labels?|post|pdf|download|binder|clipboard)\b/;

/**
 * Search (AS5): the finder's matcher over each entry's title and words. An exact or word-start title match beats a synonym; a
 * synonym beats a typo. Under 3 letters, only word starts count (so "pe" finds People, not every word with "pe" in it).
 */
export function searchEntries(entries: readonly SearchEntry[], query: string, limit = 12): SearchEntry[] {
  const q = norm(query);
  if (!q) return [];
  const scored: { e: SearchEntry; s: number; i: number }[] = [];
  entries.forEach((e, i) => {
    const title = norm(e.title);
    const terms = [title, norm(e.words.join(" ")), ...e.words.map(norm)].filter(Boolean);
    let s: number;
    if (q.length < 3) {
      const ws = terms.join(" ").split(" ");
      s = title.split(" ").some((w) => w.startsWith(q)) ? 70 : ws.some((w) => w.startsWith(q)) ? 50 : 0;
    } else {
      s = score(q, { t: terms });
      const ts = score(q, { t: [title] });
      if (ts) s = Math.max(s, ts) + 15; // the title itself matched
      else if (s > 0 && norm(e.hint).includes(q)) s += 5; // then the line under it, the words people see
    }
    // Guides and past records are many and secondary: a screen or setting with the same match comes first.
    if (s > 0 && (e.kind === "guide" || e.kind === "record")) s -= 10;
    // A printout is secondary too, until the words ask for paper: "thermometer" finds Equipment first, "thermometer sheet" the sheet.
    if (s > 0 && e.kind === "print" && !WANTS_PRINT.test(q)) s -= 30;
    if (s > 0) scored.push({ e, s, i });
  });
  return scored.sort((a, b) => b.s - a.s || KIND_ORDER[a.e.kind] - KIND_ORDER[b.e.kind] || a.i - b.i).slice(0, limit).map((x) => x.e);
}

/** Each role's usual screens, before this device knows what the person opens most (AS7). */
const USUAL: Readonly<Record<"staff" | "manager" | "home", Record<SearchPlatform, readonly string[]>>> = {
  staff: { web: ["/do", "/report", "/food", "/learn"], phone: ["/do", "/report", "/food", "/learning"] },
  manager: { web: ["/team", "/people", "/prove", "/settings"], phone: ["/team", "/people", "/prove", "/notifications"] },
  home: { web: ["/food", "/rooms", "/have"], phone: ["/home/pantry", "/rooms", "/home-have"] },
};

export interface SearchSuggestions { due: SearchEntry[]; often: SearchEntry[] }

/**
 * Before typing (AS7): today's logs that are due within the hour or late (soonest first), then the screens this person opens most
 * on this device (`usage`: entry `to` → opens), else their role's usual ones. Only entries already in this person's index.
 */
export function suggestEntries(entries: readonly SearchEntry[], o: { platform: SearchPlatform; access: SearchAccess; usage: Readonly<Record<string, number>>; now: Date; max?: number }): SearchSuggestions {
  const max = o.max ?? 4;
  const soon = o.now.getTime() + 60 * 60_000;
  const due = entries.filter((e) => e.kind === "log" && e.due && !e.due.done && Date.parse(e.due.at) <= soon)
    .sort((a, b) => Date.parse(a.due!.at) - Date.parse(b.due!.at)).slice(0, max);
  const byTo = new Map<string, SearchEntry>();
  for (const e of entries) if ((e.kind === "screen" || e.kind === "setting") && !byTo.has(e.to)) byTo.set(e.to, e);
  const used = Object.entries(o.usage).filter(([to, n]) => n > 0 && byTo.has(to)).sort((a, b) => b[1] - a[1]).map(([to]) => byTo.get(to)!);
  const usual = USUAL[o.access.home ? "home" : RANK[o.access.role] >= RANK.manager ? "manager" : "staff"][o.platform].map((to) => byTo.get(to)).filter((e): e is SearchEntry => !!e);
  const often: SearchEntry[] = [];
  for (const e of [...used, ...usual]) if (!often.includes(e) && often.length < max) often.push(e);
  return { due, often };
}

/** The usage map's key for a path: the entry it would open ("/do?check=x" counts as Checks). */
export const usageKey = (path: string): string => path.split(/[?#]/)[0] || "/";

/** One more open of `path`, kept small (the 40 most used), for "You open often" (AS7). Stored on the device only. */
export function bumpUsage(usage: Readonly<Record<string, number>>, path: string, cap = 40): Record<string, number> {
  const k = usageKey(path);
  const next = { ...usage, [k]: (usage[k] ?? 0) + 1 };
  const keys = Object.keys(next);
  if (keys.length <= cap) return next;
  return Object.fromEntries(Object.entries(next).sort((a, b) => b[1] - a[1]).slice(0, cap));
}
